import React, { createContext, type ReactNode, useContext, useMemo, useState } from 'react';

import Button, { type ButtonProps } from '@atlaskit/button/button';
import ModalBody from '@atlaskit/modal-dialog/modal-body';
import ModalDialog from '@atlaskit/modal-dialog/modal-dialog';
import ModalFooter from '@atlaskit/modal-dialog/modal-footer';
import ModalHeader from '@atlaskit/modal-dialog/modal-header';
import ModalTitle from '@atlaskit/modal-dialog/modal-title';

import { type NoticeAPI, type NoticeProps } from './types';

interface NoticeProviderProps {
	children: ReactNode;
}

interface ActionProps extends ButtonProps {
	key: string;
	text: ReactNode;
}

const ShowNoticeContext = createContext<NoticeAPI | undefined>(undefined);

export const useShowNotice: () => NoticeAPI = () => {
	const noticeApi: NoticeAPI | undefined = useContext(ShowNoticeContext);

	if (!noticeApi) {
		throw Error('Hook must be used in a NoticeProvider');
	}

	return noticeApi;
};

export const NoticeProvider: React.FC<NoticeProviderProps> = ({ children }) => {
	const [notice, setNotice] = useState<NoticeProps | undefined>();

	const onCloseHandler = () => {
		setNotice(undefined);
		if (notice && notice.onCloseHandler) {
			notice.onCloseHandler();
		}
	};

	const onConfirmHandler = () => {
		setNotice(undefined);
		if (notice && notice.onConfirmHandler) {
			notice.onConfirmHandler();
		}
	};

	const noticeApi: NoticeAPI = useMemo(
		() => ({
			showNotice: (props: NoticeProps) => {
				setNotice(props);
			},
		}),
		[],
	);

	return (
		<>
			<ShowNoticeContext.Provider value={noticeApi}>{children}</ShowNoticeContext.Provider>
			{notice && (
				<Notice {...notice} onCloseHandler={onCloseHandler} onConfirmHandler={onConfirmHandler} />
			)}
		</>
	);
};

export const Notice: React.FC<NoticeProps> = ({
	appearance,
	title,
	body,
	closeText,
	onCloseHandler,
	confirmText,
	onConfirmHandler,
}) => {
	const actions: ActionProps[] = [];
	const primaryButtonAppearance = appearance || 'primary';

	if (confirmText && onConfirmHandler) {
		actions.push({
			key: 'pf-notice-secondary-button',
			appearance: 'subtle',
			onClick: onCloseHandler,
			text: closeText,
		});
		actions.push({
			key: 'pf-notice-primary-button',
			appearance: primaryButtonAppearance,
			onClick: onConfirmHandler,
			text: confirmText,
			testId: 'show-notice-confirm-btn',
		});
	} else {
		actions.push({
			key: 'pf-notice-primary-button',
			appearance: primaryButtonAppearance,
			onClick: onCloseHandler,
			text: closeText,
			testId: 'show-notice-close-btn',
		});
	}

	return (
		<ModalDialog
			shouldCloseOnOverlayClick={false}
			onClose={onCloseHandler}
			testId="show-notice-dialog"
		>
			<ModalHeader hasCloseButton>
				<ModalTitle appearance={appearance}>{title}</ModalTitle>
			</ModalHeader>
			<ModalBody>{body}</ModalBody>
			<ModalFooter>
				{actions.map(({ key, text, ...props }) => (
					<Button key={key} {...props}>
						{text}
					</Button>
				))}
			</ModalFooter>
		</ModalDialog>
	);
};
